import { useQueryClient } from "@tanstack/react-query";

import { IPart } from "../models/Part";
import useDefaultCRUDHandlers from "../hooks/useDefaultCRUDHandlers";
import usePartMutations from "./usePartMutations";
import useOffers from "./useOffers";

/**
 * Shared logic for persisting an eBay listing title onto a part's offer.
 * Updates the existing offer when present, otherwise creates a default offer,
 * and flags the part as titleConfigured. Used by both the inline title builder
 * and the quick AI-title button so save behaviour stays consistent.
 */
export default function useSaveEbayOfferTitle() {
  const queryClient = useQueryClient();
  const { patchPart } = usePartMutations();
  const { createDefaultOffer, update } = useOffers();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Title");

  const saveTitle = (part: IPart | undefined, title: string, onSaved?: () => void) => {
    if (!part) return;

    const detailsCache: any = queryClient.getQueryData(["partDetails", part.id]);
    const partCache: any = queryClient.getQueryData(["parts", part.id]);
    const detailsPart = detailsCache?.data ?? detailsCache?.data?.data;
    const singlePart = partCache?.data ?? partCache?.data?.data;
    const latestOffers = detailsPart?.offers ?? singlePart?.offers ?? part.offers ?? [];
    let offer = latestOffers?.[0] ?? part.offers?.[0];
    const updatedOffer = {
      ...offer,
      title,
    };

    const setTitleConfigured = () => {
      if (part.titleConfigured) return;

      patchPart.mutate(
        {
          id: part.id,
          titleConfigured: true,
        },
        {
          onSuccess: () => {
            part.titleConfigured = true;
          },
        }
      );
    };

    if (offer) {
      update.mutate(updatedOffer, {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          offer.title = title;
          setTitleConfigured();
          onSaved?.();
        },
      });
    } else {
      createDefaultOffer.mutate(
        { partId: part.id, body: updatedOffer },
        {
          ...saveHandlers,
          onSuccess: (data: any) => {
            saveHandlers.onSuccess();
            const createdOffer = data?.data?.offers?.[0];
            if (createdOffer) {
              part.offers = [createdOffer];
            } else {
              part.offers = [{ ...updatedOffer }] as any;
            }
            setTitleConfigured();
            onSaved?.();
          },
        }
      );
    }
  };

  return {
    saveTitle,
    isSaving: update.isLoading || createDefaultOffer.isLoading,
  };
}
